import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'react-phone-number-input';
import CommonModal from '../../common/CommonModal';
import { Formik, Form, Field } from 'formik';
import Button from '../../common/Button';
import Grid from 'apollo-react/components/Grid';
import SwitchInput from '../../common/Form/switchInput';
import { renderInputComponent } from '../../common/Form/renderInputComponent';
import PhoneInput from '../../common/Form/PhoneInput';
import {
  fetchExternalUserById,
  createExternalUser,
  updateExternalUser,
  fetchExternalRoles,
  fetchCategories,
} from './externalUsersService';
import { checkPrivilegeOnResource } from '../../../services/authService';
import { RESOURCES } from '../../../constants/resourceConstants';
import { PRIVILEGES } from '../../../constants/privilege.constant';
import { ROLE_STATUS } from '../../../constants/role.constant';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';

const DynamicForm = ({ persona }) => {
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [formDataCopy, setFormDataCopy] = useState({});
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [initialValues, setInitialValues] = useState({});
  const [discardModal, setDiscardModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [t] = useTranslation();
  const { id } = useParams();
  const location = useLocation();

  const isDirty = useMemo(() => {
    const keys = new Set([
      ...Object.keys(formData),
      ...Object.keys(formDataCopy),
    ]);
    for (const key of keys) {
      if (String(formData[key] ?? '') !== String(formDataCopy[key] ?? ''))
        return true;
    }
    return false;
  }, [formData, formDataCopy]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  // Fetch form fields and data (if updating) from the backend
  useEffect(() => {
    const fetchFormMetadata = async () => {
      try {
        // External roles and categories (role dropdown: published_active only)
        const [rolesData, categoriesData] = await Promise.all([
          fetchExternalRoles(),
          fetchCategories(),
        ]);
        setRoles(
          rolesData.filter(
            (role) => role.status === ROLE_STATUS.PUBLISHED_ACTIVE
          )
        );
        const catItems = Array.isArray(categoriesData)
          ? categoriesData
          : categoriesData?.data || [];
        setCategories(catItems);

        const formFields = [
          {
            name: 'username',
            type: 'text',
            label: t('Username'),
            required: true,
            fullWidth: true,
            disabled: isViewMode,
          },
          {
            name: 'email',
            type: 'email',
            label: t('Email'),
            required: true,
            fullWidth: true,
            disabled: isViewMode,
          },
          {
            name: 'category_id',
            type: 'dropdown',
            label: t('registration.category'),
            required: true,
            fullWidth: true,
            disabled: isViewMode || isUpdate,
          },
          {
            name: 'role',
            type: 'dropdown',
            label: t('Role'),
            required: false,
            fullWidth: true,
            disabled: isViewMode,
          },
        ];

        setFormFields(formFields);

        if (id) {
          // If we have an ID, fetch data for updating
          setIsUpdate(true);

          const recordData = await fetchExternalUserById(id);

          // Set role from the nested object
          if (recordData.Role) {
            recordData.role = recordData.Role.id;
          }

          setFormData(recordData);
          setFormDataCopy(recordData);
        }

        setLoading(false);
      } catch (err) {
        setResp(t('errorFetchingData'));
        console.error(t('errorFetchingData'), err);
        setLoading(false);
      }
    };

    fetchFormMetadata();
  }, [id, t, isViewMode, isUpdate]);

  // Check if we're in view mode based on the URL or access privileges
  useEffect(() => {
    const viewMode = location.pathname.includes('/details/') || !hasAccess;
    setIsViewMode(viewMode);
  }, [location.pathname, hasAccess]);

  useEffect(() => {
    const values = formFields.reduce((acc, field) => {
      acc[field.name] = formData[field.name] || '';
      return acc;
    }, {});

    setInitialValues(values);
  }, [formFields, formData]);

  useEffect(() => {
    const checkUserPrivileges = async () => {
      try {
        if (persona === 'super-admin') {
          setHasAccess(true);
          return;
        }
        const privilege = isUpdate ? PRIVILEGES.EDIT : PRIVILEGES.CREATE;
        const access = await checkPrivilegeOnResource(
          privilege,
          RESOURCES.EXTERNAL_USERS
        );
        setHasAccess(access);
      } catch (err) {
        setHasAccess(false);
      }
    };

    checkUserPrivileges();
  }, [isUpdate, persona]);

  const handleChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateEmail = (email) => {
    // Regular expression to validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validate = () => {
    let errorMessage = '';
    console.log('Validating form data:', formData);
    console.log('isUpdate:', isUpdate);

    if (!formData.username) {
      errorMessage = t('usernameRequired');
    } else if (!formData.email) {
      errorMessage = t('emailRequired');
    } else if (!isUpdate && !formData.category_id) {
      errorMessage = t('validation.category.required');
    }

    // If no required field errors, check format validations
    if (!errorMessage) {
      if (formData.email && !validateEmail(formData.email)) {
        errorMessage = t('invalidEmailFormat');
      }
    }

    console.log('Validation result:', errorMessage);
    if (errorMessage) toast.error(errorMessage, { position: 'top-center' });

    return errorMessage ? false : true;
  };

  const handleSubmit = async () => {
    try {
      const isValid = validate();
      if (!isValid) return false;

      if (isUpdate) {
        if (!formData.password) delete formData.password;
        await updateExternalUser(id, formData);
        toast.success(t('externalUsers.updatedSuccessfully'), {
          position: 'top-center',
        });
        navigateBack();
      } else {
        const response = await createExternalUser(formData);
        toast.success(t('externalUsers.userAddedSuccessfully'), {
          position: 'top-center',
        });
        setToken('');
        if (response.token) {
          setToken(response.token);
        } else {
          navigateBack();
        }
      }
    } catch (err) {
      console.error('Error submitting form: ', err);
      const errorMsg = err?.response?.data?.message || t('errorSubmittingForm');
      toast.error(errorMsg, { position: 'top-center' });
      return false;
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div
      className="content"
      style={{
        border: 'solid 1px #E9E9E9',
        padding: '24px',
        borderRadius: '5px',
      }}
    >
      {resp && <p>{resp}</p>}
      <Grid container justifyContent={'center'} sx={{ width: '100%' }}>
        <Grid size={12}>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit: formikSubmit, setFieldValue }) => (
              <Form onSubmit={formikSubmit}>
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  {formFields.map((field, index) => (
                    <React.Fragment key={field.name}>
                      {/* Start a new row for every two inputs */}
                      {index % 2 === 0 && (
                        <Grid
                          container
                          spacing={2}
                          sx={{ width: '100%' }}
                        ></Grid>
                      )}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Field key={field.name} name={field.name}>
                          {({ field: formikField, meta }) => {
                            const handleFieldChange = (value) => {
                              console.log('formikField', formikField, value);
                              setFieldValue(formikField.name, value);
                              handleChange(formikField.name, value);
                            };
                            if (field.type === 'dropdown') {
                              if (field.name === 'role') {
                                if (roles.length === 0) {
                                  field.options = [
                                    {
                                      key: '',
                                      label:
                                        t('no_roles_available') ||
                                        'No roles available',
                                      disabled: true,
                                    },
                                  ];
                                } else {
                                  field.options = roles.map((role) => ({
                                    key: role.id,
                                    label: role.name,
                                  }));
                                }
                              } else if (field.name === 'category_id') {
                                if (categories.length === 0) {
                                  field.options = [
                                    {
                                      key: '',
                                      label: t(
                                        'no_categories_available',
                                        'No categories available'
                                      ),
                                      disabled: true,
                                    },
                                  ];
                                } else {
                                  field.options = categories.map((cat) => ({
                                    key: cat.id,
                                    label: cat.name,
                                  }));
                                }
                              }
                            }
                            return renderInputComponent(
                              field,
                              formData,
                              formikField,
                              meta,
                              handleFieldChange
                            );
                          }}
                        </Field>
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>

                {isUpdate && !isViewMode ? (
                  <>
                    <Field name={'active'}>
                      {({ field: formikField, meta }) => (
                        <SwitchInput
                          {...formikField}
                          meta={meta}
                          name={'active'}
                          onChange={(checked) =>
                            handleChange('active', checked)
                          }
                          label={'Active'}
                          checked={formData['active']}
                          value={formData['active']}
                          style={{
                            marginTop: '16px',
                            marginBottom: '8px',
                          }}
                        />
                      )}
                    </Field>
                  </>
                ) : isUpdate && isViewMode ? (
                  <div
                    style={{
                      marginTop: '16px',
                      marginBottom: '8px',
                    }}
                  >
                    <label
                      style={{
                        fontWeight: 'bold',
                        marginRight: '10px',
                      }}
                    >
                      Active:
                    </label>
                    <span>{formData['active'] ? 'Yes' : 'No'}</span>
                  </div>
                ) : null}

                <Field key="phone" name="phone">
                  {({ field: formikField, meta }) => (
                    <>
                      {isViewMode ? (
                        <div
                          style={{
                            marginTop: '16px',
                            marginBottom: '8px',
                          }}
                        >
                          <label
                            style={{
                              fontWeight: 'bold',
                              marginRight: '10px',
                            }}
                          >
                            Phone Number:
                          </label>
                          <span>{formData['phone'] || 'N/A'}</span>
                        </div>
                      ) : (
                        <PhoneInput
                          {...formikField}
                          meta={meta}
                          label={t('phoneNumber')}
                          value={formData['phone'] || '+20'}
                          onChange={(value) => {
                            handleChange('phone', value);
                            if (value && !isValidPhoneNumber(value)) {
                              setError('Invalid phone number');
                            } else {
                              setError('');
                            }
                          }}
                          error={error}
                        />
                      )}
                    </>
                  )}
                </Field>

                {!isViewMode && hasAccess && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: '50px',
                    }}
                  >
                    <Button
                      color="primary"
                      onClick={() => setDiscardModal(true)}
                      style={{
                        padding: '12px 22px',
                        width: '157px',
                        height: '40px',
                        marginRight: '24px',
                      }}
                      text={t('discard')}
                    />

                    <Button
                      color="primary"
                      type="submit"
                      style={{
                        padding: '12px 22px',
                        width: '157px',
                        height: '40px',
                      }}
                      text={t('Save')}
                    />
                  </div>
                )}
              </Form>
            )}
          </Formik>
          {token && (
            <div className="mt-2 p-2 card">
              <h6>Generated Token:</h6>
              <p className="">{token}</p>
            </div>
          )}
        </Grid>
      </Grid>

      {!isViewMode && (
        <CommonModal
          open={discardModal}
          message="Are you sure you want to discard your changes?"
          buttonProps={[
            {
              onClick: () => {
                setDiscardModal(false);
              },
            },
            {
              label: t('confirm'),
              onClick: () => {
                setFormData(formDataCopy);
                setDiscardModal(false);
              },
            },
          ]}
          hideHeader
          id="hideHeader"
        />
      )}
    </div>
  );
};

export default DynamicForm;
